// @ts-nocheck
import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Divider, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconTag, IconTrash, IconUsersGroup } from "@tabler/icons-react";
import { toJS } from "mobx";
import { useEffect, useState } from "react";
import UsageLimit from "@/components/common/UsageLimit/UsageLimit";
import Actions from "@/components/common/ui/button/ActionButtons/Actions";
import { crewStore } from "@/mobx/rootStore";
import { EMPTY_OBJECT } from "@/utils/common/defaults";
import { usePolymorphicData } from "@/utils/hooks/usePolymorphicData";
import CrewMembers from "../CrewMembers/CrewMembers";

/**
 * @param {object} props
 * @param {boolean} [props.opened=true]
 * @param {function} [props.onClose=() => {}]
 * @param {function} [props.onSubmit=() => {}]
 * @param {{ id?: string, name?: string, invitationId?: string }} [props.crew={}]
 * @param {boolean} [props.loading=false]
 * @param {boolean} [props.showDelete=true]
 * @param {function} [props.onDelete=() => {}]
 */
export default function CrewFields({
	opened = true,
	onClose = () => {},
	onSubmit = () => {},
	crew = EMPTY_OBJECT,
	loading = false,
	showDelete = true,
	onDelete = () => {},
}) {
	const { t } = useLingui();
	const [isEditingMembers, setIsEditingMembers] = useState(false);

	const isEditing = Boolean(crew?.id);

	const polymorphicData = usePolymorphicData({
		createLabel: t`Crear espacio`,
		editLabel: t`Editar espacio`,
		isEditing,
	});

	const form = useForm({
		initialValues: {
			name: "",
		},
		validate: {
			name: (value) => (value.length === 0 ? t`Este campo es obligatorio` : null),
		},
	});

	useEffect(() => {
		if (!crew) return;
		if (crew.id) {
			form.setValues(toJS(crew));
		} else {
			form.reset();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [crew?.id]);

	const handleConfirmDelete = (crew) => {
		modals.openConfirmModal({
			title: t`Eliminar crew`,
			centered: true,
			children: (
				<Text size="sm" span>
					<Trans>
						Esta acción eliminará "{crew.name}" de forma permanente y no se podrá
						recuperar la información.
					</Trans>
				</Text>
			),
			labels: {
				confirm: t`Eliminar crew`,
				cancel: t`Cancelar`,
			},
			confirmProps: {
				color: "red",
				leftSection: <IconTrash />,
				"data-testid": "crew-delete-confirm-btn",
			},
			cancelProps: {
				"data-testid": "crew-delete-cancel-btn",
			},
			onConfirm: () => onDelete(crew),
		});
	};

	const handleRenewInvitationId = () => {
		crewStore.fetchMyCrews();
		onClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={
				<Text span size="xl" fw="bold">
					{polymorphicData.modalTitle}
				</Text>
			}
			data-testid="crew-modal"
		>
			<form
				onSubmit={form.onSubmit((values) => {
					onSubmit(values);
				})}
			>
				<Stack>
					{crew?.id && (
						<Text size="xs" c="dimmed" opacity={0.33}>
							id {crew?.id}
						</Text>
					)}

					<TextInput
						label={t`Nombre`}
						leftSection={<IconTag />}
						placeholder={t`Nombre del espacio`}
						{...form.getInputProps("name")}
						data-testid="crew-name-input"
					/>

					{isEditing && (
						<>
							<Divider my="sm" />

							<Button
								variant="light"
								leftSection={<IconUsersGroup />}
								onClick={() => setIsEditingMembers(true)}
								data-testid="crew-manage-members-btn"
							>
								<Trans>Gestionar miembros</Trans>
							</Button>

							{showDelete && (
								<Button
									leftSection={<IconTrash />}
									color="red"
									variant="light"
									onClick={() => handleConfirmDelete(crew)}
									data-testid="crew-delete-btn"
								>
									<Trans>Eliminar espacio</Trans>
								</Button>
							)}

							<Divider my="sm" />
						</>
					)}

					{!isEditing && <UsageLimit feature="crew.maxItems" />}

					<Stack data-testid="crew-actions">
						<Actions
							showDelete={false}
							loading={loading}
							onCancel={onClose}
							submitIcon={polymorphicData.submitIcon}
							submitText={polymorphicData.submitText}
						/>
					</Stack>
				</Stack>
			</form>

			<CrewMembers
				opened={isEditingMembers}
				onClose={() => setIsEditingMembers(false)}
				crew={crew}
				onRenewInvitationId={handleRenewInvitationId}
			/>
		</Modal>
	);
}
